import { RequestMethods } from '../types/fetch/requestMethod';
import { reauthenticate } from './reauthenticate';
/**
 * Build a cookie-authenticated fetch request with corresponding params and XSRF token.
 * @param url Request url
 * @param query Json object to pass as get params
 * @param method Of type {@link RequestMethods} Default - {@link RequestMethods.Get}
 * @param body Object to send as request body. If it is json, corresponding Header will be passed automatically
 * @param headers Additional Headers if necessary
 * @param onSuccess Callback function that executes when request is successful
 * @param onError Callback function that executes if request fails
 * @param noResponseBody Flag, that indicates response should not be unwrapped
 * @param credentials Credentials mode. Default - 'same-origin'. Pass 'include' to send cookies cross-origin
 * @param signal Flag to cancel request
 * @returns {Promise<Response|*>} By default return request itself
 */
export async function enFetch(url, { method = RequestMethods.Get, query, body, headers, noResponseBody = false, credentials = 'same-origin', signal, onSuccess, onError, } = {}) {
    const finalUrl = buildUrl(url, query);
    const finalHeaders = new Headers();
    mergeHeaders(finalHeaders, headers);
    const xsrfToken = getAntiForgeryToken();
    if (xsrfToken) {
        finalHeaders.set('XSRF-TOKEN', xsrfToken);
    }
    const requestInit = {
        method,
        headers: finalHeaders,
        credentials,
    };
    // If signal for cancelling the request is passed, do it
    if (signal) {
        requestInit.signal = signal;
    }
    attachBody(requestInit, finalHeaders, body, method);
    let response;
    try {
        response = await fetch(finalUrl, requestInit);
    }
    catch (error) {
        onError?.(error);
        throw error;
    }
    // If session expired, reauthenticate for page classic calls (ie navigate to dashboard)
    if (response.type === 'cors' && response.redirected && await reauthenticate()) {
        response = await fetch(finalUrl, requestInit);
    }
    // If session expired, reauthenticate for api calls (Get/post/update)
    if (response.status === 401 && await reauthenticate()) {
        response = await fetch(finalUrl, requestInit);
    }
    if (!response.ok) {
        if (noResponseBody) {
            return response;
        }
        const errorPayload = await readErrorPayload(response);
        onError?.(errorPayload, response);
        throw new Error(typeof errorPayload === 'string'
            ? errorPayload
            : `Request failed with status ${response.status}`);
    }
    if (noResponseBody) {
        onSuccess?.(response);
        return response;
    }
    else {
        const responseBody = await response.json();
        onSuccess?.(responseBody);
        return responseBody;
    }
}
/**
 * Apply query params for the url
 * @param url Original url
 * @param query {@link QueryParams} Any key-value dictionary
 */
function buildUrl(url, query) {
    if (!query)
        return url;
    const fullUrl = new URL(url, window.location.origin);
    for (const [key, value] of Object.entries(query)) {
        if (value !== null && value !== undefined) {
            fullUrl.searchParams.set(key, String(value));
        }
    }
    return fullUrl.toString();
}
/**
 * Merge headers into one object
 * @param target Original headers that will be modified
 * @param source Headers that will be parsed and applied to {@link target}
 */
function mergeHeaders(target, source) {
    if (!source)
        return;
    const sourceHeaders = new Headers(source);
    sourceHeaders.forEach((value, key) => {
        target.set(key, value);
    });
}
/**
 * Modifies body/Headers of the request. after attaching it.
 * If body is json object, corresponding headers will be applied
 * @param requestInit Original request
 * @param headers Original headers
 * @param body Original body
 * @param method Original {@link RequestMethod}
 */
function attachBody(requestInit, headers, body, method) {
    if (body == null)
        return;
    if (method === RequestMethods.Get) {
        throw new Error('GET requests must not have a body');
    }
    if (body instanceof Date) {
        requestInit.body = body.toISOString();
        return;
    }
    if (!headers.has('Content-Type') && !(body instanceof FormData)) {
        headers.set('Content-Type', 'application/json');
    }
    if (body instanceof FormData ||
        body instanceof Blob ||
        body instanceof URLSearchParams ||
        typeof body === 'string' ||
        body instanceof ArrayBuffer ||
        ArrayBuffer.isView(body)) {
        requestInit.body = body;
        return;
    }
    requestInit.body = JSON.stringify(body);
}
/**
 * Unwrap error and return either .json() or .text()
 * @param response
 */
async function readErrorPayload(response) {
    const contentType = response.headers.get('Content-Type') ?? '';
    try {
        if (contentType.includes('application/json')) {
            return await response.json();
        }
        return await response.text();
    }
    catch {
        return `Request failed with status ${response.status}`;
    }
}
/**
 * Extract antiforgery token from <html> and apply value to the request
 */
function getAntiForgeryToken() {
    const element = document.getElementsByName('__RequestVerificationToken');
    if (!element || element.length === 0)
        return null;
    const input = element[0];
    return input.value;
}
